import { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { MapPin } from "@phosphor-icons/react";
import { HERO_IMAGE, SERVICE_AREAS } from "@/data/services";

const STATS = [
  { k: "18", suffix: " min", label: "Average arrival time, city zones" },
  { k: "240", suffix: "+", label: "Cities & highways covered" },
  { k: "1,200", suffix: "+", label: "Verified technicians on network" },
  { k: "99.2", suffix: "%", label: "Requests resolved on first visit" },
];

export const Stats = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="coverage" ref={ref} className="relative bg-rescue-bg py-24 sm:py-32 overflow-hidden">
      <motion.img
        style={{ y }}
        src={HERO_IMAGE}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover opacity-15"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-rescue-bg via-rescue-bg/80 to-rescue-bg" />

      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="max-w-2xl mb-16">
          <span className="text-rescue-primary font-semibold uppercase tracking-[0.3em] text-xs">
            Coverage that shows up
          </span>
          <h2 className="font-display uppercase tracking-tighter text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9] mt-4">
            Built to reach you before panic does.
          </h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {STATS.map((s, i) => (
            <motion.div
              key={i}
              data-testid={`stat-${i}`}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bg-rescue-bg px-6 py-10"
            >
              <div className="font-display text-6xl sm:text-7xl leading-none text-white">
                {s.k}
                <span className="text-rescue-primary">{s.suffix}</span>
              </div>
              <p className="text-white/55 text-sm mt-4 leading-relaxed">{s.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Live service areas */}
        <div className="mt-10 flex flex-col md:flex-row md:items-center gap-5 border border-white/10 bg-rescue-surface/60 p-6">
          <div className="shrink-0">
            <span className="text-rescue-primary font-semibold uppercase tracking-[0.3em] text-xs">
              Now serving
            </span>
            <div className="font-display uppercase tracking-tight text-white text-2xl leading-none mt-2">
              Live service areas
            </div>
          </div>
          <div className="flex flex-wrap gap-3 md:ml-auto">
            {SERVICE_AREAS.map((area) => (
              <span
                key={area}
                data-testid={`service-area-${area.replace(/\s+/g, "-").toLowerCase()}`}
                className="inline-flex items-center gap-2 border border-white/15 bg-rescue-bg px-4 py-2.5 rounded-sm text-white text-sm font-semibold"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-rescue-primary opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-rescue-primary" />
                </span>
                <MapPin weight="fill" size={15} className="text-rescue-primary" />
                {area}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
